import { BASE_URL } from '@synergy/config';
import { DocumentType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Locale } from '@synergy/models';
import { PlusCircleIcon } from '@synergy/theme';
import { PoolButton, PoolModal, PoolTypography } from '@synergy/components';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import urlJoin from 'url-join';

import * as ActivityActions from '../../../../../common/actions/activity-actions';
import * as ProductDetailVariantsActions from './store/product-detail-variants-actions';
import { PoolProductVariantsAutocompleteList } from '../../../../../common/components/autocomplete-list/product-variants-autocomplete-list/pool-product-variants-autocomplete-list';
import { ProductDetailVariantsTable } from './product-detail-variants-table';
import { ProductVariant } from './models/product-detail-variant';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: '24px 70px',
    },
    autocomplete: {
        marginTop: theme.spacing(2),
    },
    addButton: {
        marginTop: theme.spacing(2),
    },
}));

export interface ProductDetailVariantsProps {
    selectedLanguage: Locale;
}

export const ProductDetailVariants = (props: ProductDetailVariantsProps) => {
    const classes = useStyles();
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [unlinkModalData, setUnlinkModalData] = useState<ProductVariant | null>(null);
    const [deleteModalData, setDeleteModalData] = useState<ProductVariant | null>(null);
    const params = useParams<{ id?: string }>();

    const dispatch = useThunkDispatch();
    const variants = useAppSelector(state => state.product.detail.variants.variants);
    const locale = useAppSelector(state => bestLocaleSelector(state));

    useEffect(() => {
        if (!variants) {
            dispatch(ProductDetailVariantsActions.fetchProductVariants());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleAddClicked = () => {
        setShowAddProduct(true);
    };

    const handleProductSelected = async (productVariant: ProductVariant | null) => {
        if (!productVariant || !params.id) {
            return;
        }

        await dispatch(ProductDetailVariantsActions.saveProductVariant(productVariant));
        dispatch(ActivityActions.fetchActivities(DocumentType.Product, +params.id));

        setShowAddProduct(false);
    };

    const handleEdit = (variant: ProductVariant) => {
        if (params.id) {
            window.open(
                urlJoin(BASE_URL, ROUTES.products.product.detail.replace(':id', variant.id.toString())),
                '_blank'
            );
        }
    };

    const handleDeactivate = (variant: ProductVariant) => {
        if (variant.isActive) {
            dispatch(ProductDetailVariantsActions.deactivateProductVariant(variant.id));
        } else {
            dispatch(ProductDetailVariantsActions.activateProductVariant(variant.id));
        }
    };

    const handleDeleteModalOpen = (variant: ProductVariant) => {
        setDeleteModalData(variant);
    };

    const handleDelete = () => {
        if (deleteModalData) {
            dispatch(ProductDetailVariantsActions.deleteProductVariant(deleteModalData.id));
            setDeleteModalData(null);
        }
    };

    const handleUnlinkModalOpen = (variant: ProductVariant) => {
        setUnlinkModalData(variant);
    };

    const handleUnlink = () => {
        if (unlinkModalData) {
            dispatch(ProductDetailVariantsActions.unlinkProduct(unlinkModalData.id));
            setUnlinkModalData(null);
        }
    };

    const unlinkActionChildren = [
        <PoolButton key="1" variant="text" styleType="firstLevel" onClick={() => setUnlinkModalData(null)}>
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <PoolButton key="2" color="secondary" styleType="firstLevel" onClick={handleUnlink}>
            <FormattedMessage id="app.common.remove" />
        </PoolButton>,
    ];

    const deleteActionChildren = [
        <PoolButton key="1" variant="text" styleType="firstLevel" onClick={() => setDeleteModalData(null)}>
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <PoolButton key="2" color="secondary" styleType="firstLevel" onClick={handleDelete}>
            <FormattedMessage id="app.common.delete" />
        </PoolButton>,
    ];

    return (
        <div className={classes.container}>
            <ProductDetailVariantsTable
                variants={variants ?? []}
                selectedLanguage={props.selectedLanguage}
                onEdit={handleEdit}
                onStatusChange={handleDeactivate}
                onDelete={handleDeleteModalOpen}
                onUnlink={handleUnlinkModalOpen}
            />

            {showAddProduct && params.id && !isNaN(+params.id) && (
                <div className={classes.autocomplete}>
                    <PoolProductVariantsAutocompleteList
                        productId={+params.id}
                        onSelected={handleProductSelected}
                        clearInputOnSelect={true}
                        label={<FormattedMessage id="app.label.product" />}
                        autoFocus={true}
                        disablePreventOverflow={true}
                    />
                </div>
            )}

            <PoolButton
                startIcon={<PlusCircleIcon color="primary" />}
                onClick={handleAddClicked}
                className={classes.addButton}
            >
                <FormattedMessage id="app.product.detail.variants.add" />
            </PoolButton>

            <PoolModal
                title={<FormattedMessage id="app.product.detail.variants.unlink" />}
                isVisible={!!unlinkModalData}
                actionChildren={unlinkActionChildren}
                handleClose={() => setUnlinkModalData(null)}
                hasModalTypography={true}
            >
                <PoolTypography
                    id="app.product.detail.variants.unlinkDeleteMessage"
                    values={{ name: unlinkModalData?.name[locale] }}
                />
            </PoolModal>
            <PoolModal
                title={<FormattedMessage id="app.product.detail.variants.delete" />}
                isVisible={!!deleteModalData}
                actionChildren={deleteActionChildren}
                handleClose={() => setDeleteModalData(null)}
                hasModalTypography={true}
            >
                <PoolTypography
                    id="app.product.detail.variants.productDeleteMessage"
                    values={{ name: deleteModalData?.name[locale] }}
                />
            </PoolModal>
        </div>
    );
};
